import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/prosemirror";

import { ProseMirrorEditor } from "@tento-ui/components/prosemirror";
import { CellExpander } from "../../../shared/cell-expand/cell-expander.ts";
import { CellExpandPopup } from "../../../shared/cell-expand/cell-expand-popup.ts";

/**
 * Text-specific content factory for the generic {@link CellExpandPopup}: builds
 * a full-width popup whose content is a ProseMirror editor. The editor inherits
 * the cell's `editable` state, so the expanded view is a pure view-state
 * transform — editable for editable cells, read-only otherwise.
 *
 * Previously this file was a bespoke `TextCellPopup` web component; the popup
 * shell + dismissal + flash-avoidance now live in `CellExpandPopup`.
 */
export namespace TextCellPopup {
  export type Props = {
    inThePast: boolean;
    editable: boolean;
    markdown: Option<string>;
    onUpdate: (md: string) => void;
  };

  export async function make(
    ctx: CellExpander.PopupContext,
    props: Props,
  ): Promise<CellExpandPopup> {
    const $editor = ProseMirrorEditor.make();
    $editor.style.setProperty("--editor-padding", "10px");
    // The editor host is `display: contents`, so it has no box of its own — set
    // a definite size here so its inner editor inherits the popup height/width.
    $editor.style.height = "100%";
    $editor.style.width = "100%";

    const $popup = CellExpandPopup.make();
    $popup.bind({
      heightPx: ctx.heightPx,
      background: props.inThePast
        ? "var(--bg-hover-very-light)"
        : "var(--bg-surface-light)",
      $content: $editor,
      onWaitForLayout: () => $editor.waitForLayout(),
      onFocus: () => $editor.focusEditor(),
      onRecycleContent: () => $editor.onRecycle(),
      onDismiss: ctx.requestDismiss,
    });

    await $editor.bind({
      markdown: props.markdown ?? undefined,
      editable: props.editable,
      onUpdate: props.onUpdate,
    });

    return $popup;
  }
}
